/**
 * AreaNarrativeSection — shared editorial section for area long narrative.
 * Canonical owner: Editor portal. Reused by Admin portal.
 */
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FarmlyRichTextField } from "@/components/content/FarmlyRichTextField";
import { EditorialAiAssistant } from "@/components/ai/EditorialAiAssistant";
import type { UseFormReturn } from "react-hook-form";

interface LinkedPlace {
  place_name: string;
  place_status: string;
}

interface AreaNarrativeSectionProps {
  form: UseFormReturn<any>;
  areaName: string;
  parentAreaName?: string;
  areaType?: string;
  landskap?: string | null;
  municipality?: string | null;
  descriptionShort?: string | null;
  linkedPlaces?: LinkedPlace[];
  disabled?: boolean;
  hideAi?: boolean;
}

export function AreaNarrativeSection({
  form,
  areaName,
  parentAreaName,
  areaType,
  landskap,
  municipality,
  descriptionShort,
  linkedPlaces = [],
  disabled = false,
  hideAi = false,
}: AreaNarrativeSectionProps) {
  return (
    <FormField
      control={form.control}
      name="description_long"
      render={({ field }) => {
        const hasText = (field.value || "").trim().length > 0;
        return (
          <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel className="text-xs">Längre berättelse</FormLabel>
              {!hideAi && (
                <EditorialAiAssistant
                  task="area_long_description_draft"
                  buildContext={() => {
                    const nearbyPlaceNames = linkedPlaces
                      .filter((lp) => lp.place_status === "published")
                      .slice(0, 8)
                      .map((lp) => lp.place_name);
                    return {
                      name: areaName,
                      parentAreaName: parentAreaName || undefined,
                      areaType: areaType || undefined,
                      landskap: landskap || undefined,
                      municipality: municipality || undefined,
                      descriptionShort: descriptionShort || undefined,
                      nearbyPlaceNames:
                        nearbyPlaceNames.length > 0 ? nearbyPlaceNames : undefined,
                      currentText: field.value || undefined,
                      fieldType: "long_description",
                    };
                  }}
                  onAccept={(text) =>
                    form.setValue("description_long", text, { shouldDirty: true })
                  }
                  disabled={disabled || !areaName}
                  allowRewrite
                  hasExistingText={hasText}
                  rewriteContextOverride={{ fieldType: "long_description" }}
                  showAdvanced
                  lengthProfile="body"
                  currentText={field.value || undefined}
                />
              )}
            </div>
            <FormControl>
              <FarmlyRichTextField
                id="area-description-long"
                label=""
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Berätta om landskapet, karaktären och vad som gör området unikt."
                maxLength={5000}
                disabled={disabled}
                warnBelow={120}
                warnMessage="Kort – sikta på minst 120 tecken."
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
